import { assertNever } from "@bindery/shared/std/assert-never";
import type { CompositionEdit, CompositionToken, WordToken } from "./schema";

/** Re-says per span, across its replacements (plan § 4, Edit). */
export const RESAY_CAP = 2;

/** The ids a token answers to: its own first, then every id it superseded. */
export const tokenLineage = (token: CompositionToken): string[] =>
  token.kind === "punct" ? [token.id] : [token.id, ...token.supersedes];

/** Ids an edit names directly. */
export function editTokenIds(edit: CompositionEdit): readonly string[] {
  switch (edit.op) {
    case "insert-dictation":
    case "insert-typed":
      return [];
    case "judge":
      return edit.judgements.map((j) => j.tokenId);
    case "surface":
    case "re-say":
    case "delete":
    case "move":
      return edit.tokenIds;
    case "confirm":
    case "reject":
    case "choose":
    case "type":
    case "describe":
    case "save-for-adult":
    case "adult-resolve":
    case "build":
      return [edit.tokenId];
    default:
      return assertNever(edit, "edit");
  }
}

// Re-say and tell-me-about-it are the microphone's track for a span, so they
// follow the word through its replacements and the cap holds; every other
// rung was about the replaced word's text and says nothing about the new one.
const followsLineage = (edit: CompositionEdit): boolean =>
  edit.op === "re-say" || edit.op === "describe";

/**
 * The edits that bear on a word's place on the ladder, oldest first: every
 * edit naming the word itself, plus the re-says and descriptions of any word
 * it replaced. `lineage` is `tokenLineage(word)`, the word's own id first.
 */
export function tokenHistory(
  edits: readonly CompositionEdit[],
  lineage: readonly string[],
): CompositionEdit[] {
  const [self] = lineage;
  const ids = new Set(lineage);
  return edits.filter((e) =>
    editTokenIds(e).some(
      (id) => id === self || (followsLineage(e) && ids.has(id)),
    ),
  );
}

const heardTokens = (edit: CompositionEdit): readonly CompositionToken[] =>
  edit.op === "insert-dictation"
    ? edit.tokens
    : edit.op === "re-say" && edit.result.outcome === "replaced"
      ? edit.result.tokens
      : [];

/** The recordings a word, and every word it replaced, were heard in. */
export function recordingsOf(
  edits: readonly CompositionEdit[],
  word: WordToken,
): ReadonlySet<string> {
  const lineage = new Set(tokenLineage(word));
  return new Set([
    ...(word.asr === null ? [] : [word.asr.dictationId]),
    ...edits
      .flatMap(heardTokens)
      .flatMap((t) =>
        t.kind !== "punct" && t.asr !== null && lineage.has(t.id)
          ? [t.asr.dictationId]
          : [],
      ),
  ]);
}
